import dayjs, {Dayjs} from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Singapore");

const DefaultDisplayFormat = 'YYYY-MM-DDTHH:mm:ss';
const DateOnlyDefaultDisplayFormat = 'DD/MM/YY';
const CasualDisplayFormat = 'DD/MM/YY h:mm A';
const CasualDisplayFormatWithoutA = 'DD/MM/YYYY HH:mm';
const DateOnlyCasualDisplayFormat = 'DD/MM/YY';
const DateOnlySemanticDisplayFormat = 'DD MMM YYYY';
const DefaultDisplayWithoutT = 'YYYY-MM-DD HH:mm:ss';
const LongDateAndShortTime = 'DD MMM YYYY, HH:mm';
const LongDateAndShortTimeWithoutComma = 'DD MMM YYYY HH:mm';
const LongDateAndShortTimeWithSecond = 'DD MMM YYYY, HH:mm:ss';
const LongDateAndShortTimeWithoutCommaWithSecond = 'DD MMM YYYY HH:mm:ss';
const ShortTimeWithoutComma = 'HH:mm';

// Milli conversion
export const milliToCasualDisplay = (millisecond: any, dateOnly = false) => {
  if (millisecond === 0) {
    return null;
  }

  const dayObj = dayjs.unix(millisecond / 1000);
  return dayObj
  .utc()
  .utcOffset(8)
  .format(dateOnly ? DateOnlyCasualDisplayFormat : CasualDisplayFormat);
};

// String conversion
export const stringToDefaultDisplay = (date: string, dateOnly = false) => {
  const dayObject = dayjs(date);
  return dayObject.format(dateOnly ? DateOnlyDefaultDisplayFormat : DefaultDisplayFormat);
};

export const stringToCasualDisplay = (date: string, dateOnly = false) => {
  const dayObject = dayjs(date);
  return dayObject.format(dateOnly ? DateOnlyCasualDisplayFormat : CasualDisplayFormat);
};

export const stringToFullDateFormat = (date: string, dateOnly = false) => {
  const dayObject = dayjs(date);
  return dayObject.format(dateOnly ? DateOnlyDefaultDisplayFormat : DefaultDisplayWithoutT);
};

// dd Mmm yyyy
export const stringToSemanticDisplay = (date: string) => {
  const dayObject = dayjs(date);
  return dayObject.format(DateOnlySemanticDisplayFormat);
};
// dd Mmm yyyy
export const dayjsToSemanticDisplay = (date: Dayjs | null | undefined) => date?.format(DateOnlySemanticDisplayFormat) || '-';

// UTC conversion
export const utcToDefaultDisplay = (date: string, dateOnly = false) =>
    dayjs
    .utc(date)
    .local()
    .format(dateOnly ? DateOnlyDefaultDisplayFormat : DefaultDisplayWithoutT);

/** compare function to use with sorting */
export const compare = (date1: string, date2: string, order: 'asc' | 'desc') => {
  if (dayjs(date1).isBefore(dayjs(date2))) {
    return order === 'asc' ? -1 : 1;
  }
  if (dayjs(date1).isAfter(dayjs(date2))) {
    return order === 'asc' ? 1 : -1;
  }
  // a must be equal to b
  return 0;
};

// DD MMM YYYY, HH:mm
export const stringToLongDateAndShortTime = (date: string, withComma = false) =>
    dayjs(date).format(withComma ? LongDateAndShortTime : LongDateAndShortTimeWithoutComma);

export const stringToLongDateAndShortTimeUTCToLocal = (date: string, withComma = false) =>
    dayjs
    .utc(date)
    .local()
    .format(withComma ? LongDateAndShortTime : LongDateAndShortTimeWithoutComma);

export const stringToShortTime = (date: string, withComma = false) =>
    dayjs(date).format(withComma ? LongDateAndShortTime : ShortTimeWithoutComma);

export const casualDisplayFormatWithoutA = (date: string) => {
  const dayObject = dayjs(date);
  return dayObject.format(CasualDisplayFormatWithoutA);
};

/**
 * Converting the date string (with time zone info) to local ISO date format (yyyy-mm-dd).
 *
 * It is needed when date picker components is used in a form. Date picker will return UTC (±00:00)
 * time and pass as form data to backend. However, backend will only look at the value of
 * year/month/day instead of the time zone info. So, we should convert it before sending it to BE.
 *
 * @param targetDate target date to be converted.
 */
export const convertToLocalIsoDate = (targetDate: string): string => {
  let date = new Date(targetDate);
  const offset = date.getTimezoneOffset();
  date = new Date(date.getTime() - offset * 60 * 1000);
  const day = `0${date.getDate()}`.slice(-2);
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const year = date.getFullYear();
  return [year, month, day].join('-');
};

// DD MMM YYYY, HH:mm:ss
export const stringToLongDateAndShortTimeWithSecond = (date: string, withComma = false) =>
    dayjs(date).format(
        withComma ? LongDateAndShortTimeWithSecond : LongDateAndShortTimeWithoutCommaWithSecond
    );

export const isDateValid = (date: string) => dayjs(date).isValid();

export const getYear = (targetDate: string) => dayjs(targetDate).year();
